// =============================================================================
// VINUM - Auth API
// Migrated from Flutter AuthAPI with EXACT endpoints
// =============================================================================

import { apiPost, apiGet, apiPut, apiPatch, setAuthTokens, clearAuthTokens, getRefreshToken } from './client';
import type {
  User,
  LoginResponse,
  RegisterPayload,
  UserState,
  AuthTokens,
} from '../types';

// -----------------------------------------------------------------------------
// Auth API Endpoints
// Exact match with Flutter auth_api.dart
// -----------------------------------------------------------------------------

export const AuthAPI = {
  /**
   * Login with email and password
   * POST /auth/login
   *
   * Backend returns: { user, accessToken, refreshToken, message }
   * We normalize to: { user, tokens: { accessToken, refreshToken }, message }
   */
  async login(email: string, password: string): Promise<LoginResponse> {
    // Backend returns flat structure: { accessToken, refreshToken, user }
    const rawResponse = await apiPost<{
      user: User;
      accessToken: string;
      refreshToken: string;
      message?: string;
    }>('/auth/login', { email, password });

    // Normalize to expected structure
    const tokens: AuthTokens = {
      accessToken: rawResponse.accessToken,
      refreshToken: rawResponse.refreshToken,
    };

    // Save tokens
    await setAuthTokens(tokens);

    // Return normalized response
    return {
      user: rawResponse.user,
      tokens,
      message: rawResponse.message,
    };
  },

  /**
   * Register new user
   * POST /auth/register
   */
  async register(payload: RegisterPayload): Promise<{ message: string; user?: User }> {
    return apiPost('/auth/register', payload);
  },

  /**
   * Logout current user
   * POST /auth/logout
   * Backend requires refreshToken in body
   */
  async logout(): Promise<void> {
    try {
      const token = getRefreshToken();
      if (token) {
        await apiPost('/auth/logout', { refreshToken: token });
      }
    } finally {
      await clearAuthTokens();
    }
  },

  /**
   * Refresh access token
   * POST /auth/refresh
   */
  async refreshToken(refreshToken: string): Promise<AuthTokens> {
    const response = await apiPost<AuthTokens>('/auth/refresh', { refreshToken });
    await setAuthTokens(response);
    return response;
  },

  /**
   * Check if email exists
   * POST /auth/check-email
   */
  async checkEmailExists(email: string): Promise<{ exists: boolean; message?: string }> {
    return apiPost('/auth/check-email', { email });
  },

  /**
   * Check if user identifier (email or phone) exists
   * POST /auth/check-user
   * Used in password reset flow to verify user exists before sending OTP
   */
  async checkUserIdentifier(identifier: string): Promise<{ success: boolean; message?: string }> {
    return apiPost('/auth/check-user', { identifier });
  },

  /**
   * Get user state by email (for registration flow)
   * GET /auth/user-state?email=
   */
  async getUserStateByEmail(email: string): Promise<UserState> {
    return apiGet('/auth/user-state', {
      params: { email },
    });
  },

  /**
   * Request password reset (sends OTP via email)
   * POST /auth/forgot-password
   *
   * Backend expects: { identifier: string, method: 'email' }
   * Aligned with Flutter implementation (auth_api.dart)
   */
  async forgotPassword(identifier: string): Promise<{ success: boolean; message: string }> {
    return apiPost('/auth/forgot-password', {
      identifier,
      method: 'email',
    });
  },

  /**
   * Verify OTP code for password reset
   * POST /auth/verify-reset-code
   *
   * Backend expects: { identifier: string, code: string }
   * Returns 204 if valid, 401 if invalid, 429 if too many wrong attempts
   */
  async verifyResetCode(identifier: string, code: string): Promise<boolean> {
    try {
      await apiPost('/auth/verify-reset-code', { identifier, code });
      return true;
    } catch (error: any) {
      if (error?.status === 401 || error?.message?.includes('401')) {
        return false;
      }
      throw error;
    }
  },

  /**
   * Reset password with the OTP code received by email
   * POST /auth/reset-password
   *
   * Backend expects: { identifier: string, code: string, newPassword: string }
   * The code is verified again server side; all sessions are revoked on success.
   */
  async resetPassword(identifier: string, code: string, newPassword: string): Promise<{ message: string }> {
    return apiPost('/auth/reset-password', {
      identifier,
      code,
      newPassword,
    });
  },

  /**
   * Get current user profile
   * GET /auth/me
   */
  async getCurrentUser(): Promise<User> {
    return apiGet('/auth/me');
  },

  /**
   * Update user profile
   * PUT /auth/profile
   */
  async updateProfile(data: Partial<User>): Promise<User> {
    return apiPut('/auth/profile', data);
  },

  /**
   * Change password
   * POST /auth/change-password
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    return apiPost('/auth/change-password', {
      currentPassword,
      newPassword,
    });
  },

  /**
   * Delete account
   * DELETE /auth/account
   */
  async deleteAccount(): Promise<{ message: string }> {
    const response = await apiPost<{ message: string }>('/auth/delete-account');
    await clearAuthTokens();
    return response;
  },

  /**
   * Register FCM token for push notifications
   * POST /auth/fcm-token
   */
  async registerFcmToken(token: string): Promise<{ message: string }> {
    return apiPost('/auth/fcm-token', { token });
  },

  /**
   * Mark tutorial as completed
   * PATCH /users/:id/tutorial-completed
   */
  async completeTutorial(userId: string): Promise<{ success: boolean }> {
    return apiPatch(`/users/${userId}/tutorial-completed`);
  },

  /**
   * Reset tutorial (to replay)
   * PATCH /users/:id/tutorial-reset
   */
  async resetTutorial(userId: string): Promise<{ success: boolean }> {
    return apiPatch(`/users/${userId}/tutorial-reset`);
  },
};
